import React, { createContext, useState, useContext, useEffect } from 'react';
import { exportToXLSX, loadFromExcel } from '../utils/ExcelUtils';
import { useVerbatim } from './VerbatimContext';  // Import the Verbatim context
import { saveToLocalStorage, loadFromLocalStorage } from '../utils/StorageUtils';

// Create context for Setup
const SetupContext = createContext();

// Custom hook to use the Setup context
export const useSetup = () => useContext(SetupContext);

export const SetupProvider = ({ children }) => {
  const [labels, setLabels] = useState({});
  const [labelTypes, setLabelTypes] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState(''); // Global status message

  const { verbatims, setVerbatims, updateLabelInVerbatims, updateAllLabelsInVerbatims, deleteLabelInVerbatims } = useVerbatim();  // Get the updatelabelinverbatims and delete function from VerbatimContext

  // Default label types and their properties
  const defaultLabelTypes = {
    Gender: { mandatory: true, sentimentEnabled: false, multiValue: false },
    Age: { mandatory: true, sentimentEnabled: false, multiValue: false },
    Sentiment: { mandatory: true, sentimentEnabled: true, multiValue: false },
    Sources: { mandatory: true, sentimentEnabled: false, multiValue: false },
  };

  // Default labels for predefined categories
  const defaultLabels = {
    Gender: [
      { name: 'Woman', state: 'active' },
      { name: 'Man', state: 'active' },
      { name: 'Unknown Gender', state: 'active' },
    ],
    Age: [
      { name: '<25', state: 'active' },
      { name: '25-34', state: 'active' },
      { name: '35-44', state: 'active' },
      { name: '45-54', state: 'active' },
      { name: '55+', state: 'active' },
      { name: 'Unknown Age', state: 'active' },
    ],
    Sentiment: [
      { name: 'positive', state: 'positive' },
      { name: 'negative', state: 'negative' },
      { name: 'mixed', state: 'mixed' },
      { name: 'neutral', state: 'neutral' },
    ],
    Sources:[
      {
        name: "Blog",
        keywords: [
          "ameblo.*",
          "blog.daum.*",
          "blogger.*",
          "medium.*",
          "naver.*",
          "over-blog.*",
          "pinterest.*",
          "tistory.*",
          "tumblr.*"
        ],
        explanation: ""
      },
      {
        name: "Douyin",
        keywords: ["douyin.*"],
        explanation: ""
      },
      {
        name: "Facebook",
        keywords: ["facebook.*"],
        explanation: ""
      },
      {
        name: "Forum",
        keywords: [
          "chefkoch.*",
          "douban.*",
          "eatsmarter.*",
          "forum.bonnegueule.*",
          "forum.purseblog.*",
          "gutefrage.*",
          "hwahae.*",
          "incenza.*",
          "instiz.*",
          "mouthshut.*",
          "parfumdo.*",
          "poshmark.*",
          "reddit.*",
          "theqoo.*",
          "tendance-parfums.*",
          "vinted.*",
          "zhihu.*",
          "zigzag.*"
        ],
        explanation: ""
      },
      {
        name: "Instagram",
        keywords: ["instagram.*"],
        explanation: ""
      },
      {
        name: "Review",
        keywords: [
          "11st.*",
          "1919.*",
          "amazon.*",
          "avocadostore.*",
          "beautybay.*",
          "beautylish.*",
          "bestbuy.*",
          "bleulibellule.*",
          "boots.*",
          "brandi.*",
          "bloomingdales.*",
          "costco.*",
          "coursesu.*",
          "credobeauty.*",
          "coupang.*",
          "cultbeauty.*",
          "cvs.*",
          "dermstore.*",
          "debenhams.*",
          "dm.*",
          "docmorris.*",
          "douglas.*",
          "feelunique.*",
          "flaconi.*",
          "flipkart.*",
          "fragrancenet.*",
          "fragrantica.*",
          "global.interpark.*",
          "glowpick.*",
          "grove.*",
          "jd.*",
          "jiomart.*",
          "kaufland.*",
          "kjaerweis.*",
          "kmall24.*",
          "kohls.*",
          "kurly.*",
          "laredoute.*",
          "lookfantastic.*",
          "macys.*",
          "maisonsdumonde.*",
          "manomano.*",
          "marionnaud.*",
          "makeupalley.*",
          "mediamarkt.*",
          "meesho.*",
          "myntra.*",
          "musinsa.*",
          "nocibe.*",
          "nordstrom.*",
          "oliveyoung.*",
          "perfumania.*",
          "purplle.*",
          "riteaid.*",
          "rossmann.*",
          "saturn.*",
          "secoo.*",
          "sephora.*",
          "shop-apotheke.*",
          "shopping.naver.*",
          "snapdeal.*",
          "spacenk.*",
          "stylevana.*",
          "suning.*",
          "superdrug.*",
          "taobao.*",
          "target.*",
          "thedetoxmarket.*",
          "thrivemarket.*",
          "tirabeauty.*",
          "tmall.*",
          "ulta.*",
          "untappd.*",
          "walgreens.*",
          "walmart.*",
          "yesmywine.*",
          "yesstyle.*",
          "fr.trustpilot.*"
        ],
        explanation: ""
      },
      {
        name: "TikTok",
        keywords: ["tiktok.*"],
        explanation: ""
      },
      {
        name: "Weibo",
        keywords: ["weibo.*"],
        explanation: ""
      },
      {
        name: "X",
        keywords: ["x.*"],
        explanation: ""
      },
      {
        name: "Xiaohongshu",
        keywords: ["xiaohongshu.*"],
        explanation: ""
      },
      {
        name: "YouTube",
        keywords: ["youtube.*"],
        explanation: ""
      }
    ]
    // Sources: [
    //   {
    //     name: "Blog",
    //     keywords: [
    //       "blog.daum.net",
    //       "blogger.com",
    //       "tumblr.com",
    //       "medium.com",
    //       "ameblo.jp",
    //       "over-blog.com",
    //       "tistory.com",
    //       "pinterest.com",
    //       "naver.com"
    //     ],
    //     explanation: ""
    //   },
    //   {
    //     name: "Review",
    //     keywords: [
    //       "coursesu.com",
    //       "ulta.com",
    //       "amazon.in",
    //       "manomano.fr",
    //       "amazon.it",
    //       "tirabeauty.com",
    //       "kurly.com",
    //       "debenhams.com",
    //       "mediamarkt.de",
    //       "perfumania.com",
    //       "sephora.co.uk",
    //       "sephora.fr",
    //       "tmall.com",
    //       "sephora.cn",
    //       "glowpick.com",
    //       "lookfantastic.com",
    //       "bleulibellule.com",
    //       "shopping.naver.com",
    //       "beautybay.com",
    //       "cultbeauty.co.uk",
    //       "boots.com",
    //       "shop-apotheke.com",
    //       "secoo.com",
    //       "oliveyoung.co.kr",
    //       "target.com",
    //       "snapdeal.com",
    //       "spacenk.com",
    //       "nocibe.fr",
    //       "jd.com",
    //       "suning.com",
    //       "beautylish.com",
    //       "flipkart.com",
    //       "thrivemarket.com",
    //       "walgreens.com",
    //       "flaconi.de",
    //       "taobao.com",
    //       "11st.co.kr",
    //       "docmorris.de",
    //       "meesho.com",
    //       "amazon.fr",
    //       "untappd.com",
    //       "marionnaud.fr",
    //       "thedetoxmarket.com",
    //       "grove.co",
    //       "yesstyle.com",
    //       "macys.com",
    //       "musinsa.com",
    //       "tendance-parfums.com",
    //       "purplle.com",
    //       "superdrug.com",
    //       "walmart.com",
    //       "fragrancenet.com",
    //       "kmall24.com",
    //       "feelunique.com",
    //       "1919.cn",
    //       "makeupalley.com",
    //       "brandi.co.kr",
    //       "amazon.com",
    //       "nykaa.com",
    //       "kjaerweis.com",
    //       "laredoute.fr",
    //       "global.interpark.com",
    //       "douglas.de",
    //       "dm.de",
    //       "kaufland.de",
    //       "credobeauty.com",
    //       "amazon.de",
    //       "coupang.com",
    //       "riteaid.com",
    //       "fragrantica.fr",
    //       "bestbuy.com",
    //       "stylevana.com",
    //       "dermstore.com",
    //       "avocadostore.de",
    //       "myntra.com",
    //       "nordstrom.com",
    //       "saturn.de",
    //       "kohls.com",
    //       "rossmann.de",
    //       "jiomart.com",
    //       "cvs.com",
    //       "yesmywine.com",
    //       "bloomingdales.com",
    //       "costco.com",
    //       "maisonsdumonde.com"
    //     ],
    //     explanation: ""
    //   },
    //   {
    //     name: "Forum",
    //     keywords: [
    //       "instiz.net",
    //       "eatsmarter.de",
    //       "gutefrage.net",
    //       "incenza.com",
    //       "reddit.com",
    //       "hwahae.co.kr",
    //       "douban.com",
    //       "mouthshut.com",
    //       "theqoo.net",
    //       "zhihu.com",
    //       "poshmark.com",
    //       "vinted.fr",
    //       "forum.bonnegueule.fr",
    //       "zigzag.kr",
    //       "parfumdo.com",
    //       "tendance-parfums.com",
    //       "forum.purseblog.com",
    //       "chefkoch.de"
    //     ],
    //     explanation: ""
    //   },
    //   {
    //     name: "YouTube",
    //     keywords: [
    //       "youtube.com"
    //     ],
    //     explanation: ""
    //   },
    //   {
    //     name: "TikTok",
    //     keywords: [
    //       "tiktok.com"
    //     ],
    //     explanation: ""
    //   },
    //   {
    //     name: "Instagram",
    //     keywords: [
    //       "instagram.com"
    //     ],
    //     explanation: ""
    //   },
    //   {
    //     name: "X",
    //     keywords: [
    //       "x.com"
    //     ],
    //     explanation: ""
    //   },
    //   {
    //     name: "Facebook",
    //     keywords: [
    //       "facebook.com"
    //     ],
    //     explanation: ""
    //   },
    //   {
    //     name: "Xiaohongshu",
    //     keywords: [
    //       "xiaohongshu.com"
    //     ],
    //     explanation: ""
    //   },
    //   {
    //     name: "Douyin",
    //     keywords: [
    //       "douyin.com",
    //     ],
    //     explanation: ""
    //   },
    //   {
    //     name: "Weibo",
    //     keywords: [
    //       "weibo.com"
    //     ],
    //     explanation: ""
    //   },
    //   {
    //     name: "Bilibili",
    //     keywords: [
    //       "bibibili.com"
    //     ],
    //     explanation: ""
    //   }
    // ]
  };

  // Load saved labels and types from IndexedDB when the component is mounted
  useEffect(() => {
    const loadSetupData = async () => {
      try {
        const data = await loadFromLocalStorage('setupData');
        const loadedLabels = data?.labels || {};
        const loadedLabelTypes = data?.labelTypes || {};
        
        setLabels({ ...defaultLabels, ...loadedLabels });
        setLabelTypes({ ...defaultLabelTypes, ...loadedLabelTypes });
        
        setIsLoaded(true);
        setIsLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données de configuration:', error);
        
        // En cas d'erreur, utiliser les valeurs par défaut
        setLabels(defaultLabels);
        setLabelTypes(defaultLabelTypes);
        
        setIsLoaded(true);
        setIsLoading(false);
      }
    };
    
    loadSetupData();
  }, []);

  // Save labels and label types to IndexedDB when changes are made
  useEffect(() => {
    const saveSetupData = async () => {
      if (isLoaded) {
        try {
          await saveToLocalStorage('setupData', { labels, labelTypes });
        } catch (error) {
          console.error('Erreur lors de la sauvegarde des données de configuration:', error);
        }
      }
    };
    
    saveSetupData();
  }, [labels, labelTypes, isLoaded]);

  // Function to show success message globally
  const showSuccessMessage = (message) => {
    setStatusMessage(message);
    setTimeout(() => setStatusMessage(''), 3000); // Clear message after 3 seconds
  };

// Function to update label type name and affected verbatims at the level of the label type table
const handleEditLabelType = (oldLabelType, newLabelType) => {
  if (newLabelType && oldLabelType !== newLabelType) {
    // Check if the newLabelType already exists in the labels
    const doesNewLabelTypeExist = labelTypes.hasOwnProperty(newLabelType);

    // Get the sentiment status of both old and new types
    const oldTypeSentimentEnabled = labelTypes[oldLabelType]?.sentimentEnabled;
    let newTypeSentimentEnabled = labelTypes[newLabelType]?.sentimentEnabled;

    // Determine if sentiment is being changed
    const sentimentChangingFromEnabledToDisabled = oldTypeSentimentEnabled && !newTypeSentimentEnabled;
    const sentimentChangingFromDisabledToEnabled = !oldTypeSentimentEnabled && newTypeSentimentEnabled;

    // Confirmation prompt for changing sentiment settings (if applicable)
    if (sentimentChangingFromEnabledToDisabled) {
      const confirmed = window.confirm(
        `You are changing the label type from a sentiment-enabled type (${oldLabelType}) to a non-sentiment-enabled type (${newLabelType}). This means the sentiment state of the labels will change from "positive", "negative", or "mixed" to "active".\n\nDo you want to proceed with this change?`
      );
      if (!confirmed) return;
    } else if (sentimentChangingFromDisabledToEnabled) {
      const confirmed = window.confirm(
        `You are changing the label type from a non-sentiment-enabled type (${oldLabelType}) to a sentiment-enabled type (${newLabelType}). This means the labels will now have sentiment states, and the current labels will be set to "positive".\n\nDo you want to proceed with this change?`
      );
      if (!confirmed) return;
    } else if (!doesNewLabelTypeExist && oldTypeSentimentEnabled) {
      // In the case of renaming to a new label type that does not exist, but sentiment-enabled status remains
      const confirmed = window.confirm(
        `You are renaming the sentiment-enabled label type (${oldLabelType}) to a new label type (${newLabelType}). The sentiment states will remain as they are.\n\nDo you want to proceed with this change?`
      );
      if (!confirmed) return;
    }

    // If the new label type doesn't exist and the old type was sentiment-enabled, set the new type to sentiment-enabled
    if (!doesNewLabelTypeExist && oldTypeSentimentEnabled) {
      newTypeSentimentEnabled = true; // Inherit sentimentEnabled status from the old label type
    }

    // Update labels by merging labels from oldLabelType to newLabelType
    setLabels((prevLabels) => {
      const updatedLabels = { ...prevLabels };

      if (updatedLabels[oldLabelType]) {
        // If newLabelType already exists, merge the labels from oldLabelType
        if (updatedLabels[newLabelType]) {
          updatedLabels[newLabelType] = [
            ...updatedLabels[newLabelType], // Keep existing labels under newLabelType
            ...updatedLabels[oldLabelType], // Add the labels from oldLabelType
          ];
        } else {
          // Otherwise, just move the labels from oldLabelType to newLabelType
          updatedLabels[newLabelType] = updatedLabels[oldLabelType];
        }

        // Remove old label type from labels
        delete updatedLabels[oldLabelType];
      }

      return updatedLabels;
    });

    // Update state for labelTypes: Keep only the parameters of the newLabelType
    setLabelTypes((prevLabelTypes) => {
      const updatedLabelTypes = { ...prevLabelTypes };

      // If the newLabelType already exists, we keep its parameters and remove oldLabelType
      if (doesNewLabelTypeExist) {
        // Remove old label type
        delete updatedLabelTypes[oldLabelType];
      } else {
        // If newLabelType doesn't exist, we overwrite oldLabelType's properties with newLabelType
        updatedLabelTypes[newLabelType] = { 
          ...updatedLabelTypes[oldLabelType], 
          sentimentEnabled: newTypeSentimentEnabled || false,  // Ensure sentimentEnabled is correctly set
        };
        delete updatedLabelTypes[oldLabelType];
      }

      return updatedLabelTypes;
    });

    // Update verbatims with the new label type and manage sentiment state
    updateAllLabelsInVerbatims(oldLabelType, newLabelType, oldTypeSentimentEnabled, newTypeSentimentEnabled);
  }
};

// Handle editing an existing label, allowing changes to its name or type at the level of the label table
// This function handles the complete logic for editing a label, including changing its type or name.
// It also manages the confirmation prompts for changing sentiment settings and creating new label types if needed.
const handleEditLabel = (label, currentLabelType, newLabelName, newLabelType) => {
  // Safeguard: Prevent proceeding if the new label name or type is empty
  if (!newLabelName.trim()) {
    alert('Label name cannot be empty. Please provide a valid name.');
    return;
  }

  if (!newLabelType.trim()) {
    alert('Label type cannot be empty. Please select or provide a valid label type.');
    return;
  }

  // // Find the current label type containing the label to be edited
  // const currentLabelType = Object.keys(labels).find((type) =>
  //   Array.isArray(labels[type]) && labels[type].some((l) => l.name === label)
  // );

  if (!currentLabelType) {
    console.error(`Label "${label}" not found in any label type.`);
    return;
  }

  // ** Check if the new label name already exists within the same label type **
  const nameConflict = Array.isArray(labels[newLabelType]) && labels[newLabelType].some((l) => l.name === newLabelName);

  if (nameConflict && newLabelType === currentLabelType) {
    alert(`The label name "${newLabelName}" already exists within the label type "${newLabelType}". Please choose a different name.`);
    return;
  }

  // Gather type data for the current and new label types
  const oldTypeData = labelTypes[currentLabelType];
  const newTypeData = labelTypes[newLabelType] || { sentimentEnabled: false };

  let isSentimentChanged = false;

  // Handle sentiment state change confirmations
  if (oldTypeData.sentimentEnabled && !newTypeData.sentimentEnabled) {
    const confirmed = window.confirm(
      'The label type is being changed from sentiment-enabled to non-sentiment-enabled. The state of the label will change to "active".'
    );
    if (!confirmed) return;
    isSentimentChanged = true;
  } else if (!oldTypeData.sentimentEnabled && newTypeData.sentimentEnabled) {
    const confirmed = window.confirm(
      'The label type is being changed from non-sentiment-enabled to sentiment-enabled. The state of the label will default to "positive".'
    );
    if (!confirmed) return;
    isSentimentChanged = true;
  }

  // If the new label type does not exist, prompt user to create it
  if (!Object.keys(labelTypes).includes(newLabelType)) {
    const confirmNewType = window.confirm(
      `The label type "${newLabelType}" does not exist. Do you want to create it?`
    );
    if (!confirmNewType) return;

    // Create the new label type with default properties
    setLabelTypes((prevTypes) => ({
      ...prevTypes,
      [newLabelType]: { mandatory: false, sentimentEnabled: false, multiValue: false },
    }));
  }

  // ** Updated Call **: Update the label in verbatims (account for both name and type change)
  updateLabelInVerbatims(label, newLabelName, currentLabelType, newLabelType);

  // Update labels state to reflect the edited label
  setLabels((prevLabels) => {
    const updatedLabels = { ...prevLabels };

    // ** Find the original label object to preserve keywords and other properties **
    const labelToUpdate = updatedLabels[currentLabelType].find((l) => l.name === label);
    if (!labelToUpdate) {
      console.error(`Label "${label}" not found in current label type.`);
      return updatedLabels;
    }

    const { keywords = [] } = labelToUpdate; // Extract the keywords, default to empty array if undefined

    // Remove the original label from the current label type
    updatedLabels[currentLabelType] = updatedLabels[currentLabelType].filter((l) => l.name !== label);

    const newState = newTypeData.sentimentEnabled ? 'positive' : 'active';

    // Add the updated label to the new type if the type has changed
    if (newLabelType !== currentLabelType) {
      if (!updatedLabels[newLabelType]) {
        updatedLabels[newLabelType] = [];
      }
      updatedLabels[newLabelType].push({
        ...labelToUpdate, // Spread the original label data to preserve keywords and other properties
        name: newLabelName,
        type: newLabelType, // Update type if needed
        state: newState,
        keywords, // Preserve keywords
      });
    } else {
      // Otherwise, just update the label within the same type
      updatedLabels[currentLabelType].push({
        ...labelToUpdate, // Spread the original label data to preserve keywords
        name: newLabelName,
        state: newState,
        type: currentLabelType, // Ensure the type is maintained
        keywords, // Preserve keywords
      });
    }

    // Display success message
    showSuccessMessage('Label updated successfully!');
    return updatedLabels;
  });
};

  // Handle changes to mandatory status for label types
  const handleMandatoryChange = (type, value) => {
    const confirmation = window.confirm(`Are you sure you want to change the mandatory setting for ${type}?`);
    if (confirmation) {
      // Proceed with the change if the user confirms
      const updatedLabelTypes = {
        ...labelTypes,
        [type]: {
          ...labelTypes[type],
          mandatory: value === 'true',
        },
      };
      setLabelTypes(updatedLabelTypes);
    }
  };

  // Handle changes to sentiment-enabled status for label types
const handleSentimentChange = (type, value) => {
  // Ask for confirmation before changing the sentiment setting for the label type
  const confirmation = window.confirm(`Are you sure you want to change the sentiment setting for ${type}?`);

  if (confirmation) {
    // Get the current sentimentEnabled status before the change
    const oldSentimentEnabled = labelTypes[type]?.sentimentEnabled;

    // Proceed with the change if the user confirms
    const updatedLabelTypes = {
      ...labelTypes,  // Keep the current label types unchanged
      [type]: {  // Update the specific label type that was changed
        ...labelTypes[type],  // Keep the other properties of the label type unchanged
        sentimentEnabled: value === 'true',  // Update sentimentEnabled based on the new value (convert to boolean)
      },
    };

    // Update the state with the new label types
    setLabelTypes(updatedLabelTypes);

    // Get the updated sentimentEnabled status after the change
    const newSentimentEnabled = updatedLabelTypes[type].sentimentEnabled;

    // Now we need to update all verbatims for this label type due to the sentiment status change
    updateAllLabelsInVerbatims(type, type, oldSentimentEnabled, newSentimentEnabled); // Pass both old and new sentiment status

    console.log(verbatims); // Debugging output
  }
};

  // Handle changes to multi-value status for label types
  const handleMultiValueChange = (type, value) => {
    const confirmation = window.confirm(`Are you sure you want to change the multi-value setting for ${type}?`);
    if (confirmation) {
      // Proceed with the change if the user confirms
      const updatedLabelTypes = {
        ...labelTypes,
        [type]: {
          ...labelTypes[type],
          multiValue: value === 'true',
        },
      };
      setLabelTypes(updatedLabelTypes);
    }
  };

  const addLabel = (newLabel, newLabelType, keywords = '', explanation = '') => {
    // Ensure newLabel and newLabelType are strings before trimming
    if (typeof newLabel !== 'string' || typeof newLabelType !== 'string') {
      alert('Invalid label name or label type. Both must be strings.');
      return false; // Return false on failure
    }
  
    const trimmedLabel = newLabel.trim();
    const trimmedLabelType = newLabelType.trim();
  
    // Check if trimmedLabel and trimmedLabelType are not empty
    if (!trimmedLabel || !trimmedLabelType) {
      alert('Label name or type cannot be empty.');
      return false; // Return false on failure
    }
  
    // Process keywords into an array of trimmed strings, excluding empty entries
    const keywordArray = keywords
      .split(';')
      .map((keyword) => keyword.trim())
      .filter((keyword) => keyword);
  
    let isLabelAdded = false;
  
    setLabels((prevLabels) => {
      const updatedLabels = { ...prevLabels };
  
      // Ensure the label type exists, or initialize it with an empty array
      if (!updatedLabels[trimmedLabelType]) {
        updatedLabels[trimmedLabelType] = [];
      }
  
      // Check for duplicate labels within the label type
      const existingLabel = updatedLabels[trimmedLabelType].find(
        (labelObj) => labelObj.name.toLowerCase() === trimmedLabel.toLowerCase()
      );
  
      if (existingLabel) {
        alert(`Label "${trimmedLabel}" already exists under the label type "${trimmedLabelType}".`);
      } else {
        // Add the new label to the label type with the specified properties
        updatedLabels[trimmedLabelType].push({
          name: trimmedLabel,
          keywords: keywordArray,
          explanation: explanation.trim(),
          mandatory: false,
          sentimentEnabled: false,
        });
  
        isLabelAdded = true; // Mark label as added successfully
      }
  
      return updatedLabels;
    });
  
    // Handle adding a new label type if it doesn't already exist
    setLabelTypes((prevLabelTypes) => {
      const updatedLabelTypes = { ...prevLabelTypes };
  
      // If the label type is new, initialize it with default properties
      if (!updatedLabelTypes[trimmedLabelType]) {
        updatedLabelTypes[trimmedLabelType] = {
          mandatory: false,
          sentimentEnabled: false,
          multiValue: true, // Default properties
        };
      }
  
      return updatedLabelTypes;
    });
  
    // Notify the caller if the label was successfully added
    if (isLabelAdded) {
      console.log(`Label "${trimmedLabel}" was successfully added under type "${trimmedLabelType}".`);
      return true; // Indicate success
    }
  
    return false; // Indicate failure
  };
  

  const mergeLabelTypes = (typeA, typeB, newTypeName, newTypeOptions, selectedLabels, verbatimTaggingMap) => {
    const normalizedNewTypeName = typeof newTypeName === 'string' ? newTypeName.trim() : '';
    const validSentimentStates = ['positive', 'negative', 'mixed'];

    if (!normalizedNewTypeName) {
      alert('New label type name cannot be empty.');
      return;
    }

    const impactedWithoutFinalTags = verbatims.filter((verbatim) => {
      const hasSourceTags = (verbatim.labels?.[typeA] || []).length > 0 || (verbatim.labels?.[typeB] || []).length > 0;
      const newTags = verbatimTaggingMap[verbatim.id];
      return hasSourceTags && (!Array.isArray(newTags) || newTags.length === 0);
    });

    if (impactedWithoutFinalTags.length > 0) {
      alert(`Cannot merge label types. ${impactedWithoutFinalTags.length} impacted verbatim(s) still need a final tag.`);
      return;
    }

    const normalizedSelectedLabels = selectedLabels.map((label) => ({
      ...label,
      type: normalizedNewTypeName,
    }));

    // Re-tag verbatims : supprimer typeA et typeB, appliquer les nouveaux tags
    const updatedVerbatims = verbatims.map((verbatim) => {
      const updatedLabels = { ...verbatim.labels };
      delete updatedLabels[typeA];
      delete updatedLabels[typeB];

      const newTags = verbatimTaggingMap[verbatim.id];
      if (newTags && newTags.length > 0) {
        updatedLabels[normalizedNewTypeName] = newTags.map((tag) => ({
          ...tag,
          type: normalizedNewTypeName,
          state: newTypeOptions.sentimentEnabled
            ? (validSentimentStates.includes(tag.state) ? tag.state : 'positive')
            : 'active',
        }));
      }
      // Empty newTags are allowed only for verbatims that were not tagged by either source type.

      return { ...verbatim, labels: updatedLabels };
    });

    // Mettre à jour labels
    const updatedLabels = { ...labels };
    delete updatedLabels[typeA];
    delete updatedLabels[typeB];
    updatedLabels[normalizedNewTypeName] = normalizedSelectedLabels;

    // Mettre à jour labelTypes
    const updatedLabelTypes = { ...labelTypes };
    delete updatedLabelTypes[typeA];
    delete updatedLabelTypes[typeB];
    updatedLabelTypes[normalizedNewTypeName] = newTypeOptions;

    setVerbatims(updatedVerbatims);
    setLabels(updatedLabels);
    setLabelTypes(updatedLabelTypes);

    showSuccessMessage(`Label types "${typeA}" and "${typeB}" merged into "${normalizedNewTypeName}" successfully.`);
  };

  const deleteLabelType = (labelType) => {
    // First, delete the labels from all affected verbatims
    deleteLabelInVerbatims(null, labelType); // Call with labelType to remove all labels of this type
  
    // Update labels and labelTypes state
    setLabels((prevLabels) => {
      const updatedLabels = { ...prevLabels };
      delete updatedLabels[labelType]; // Remove the entire labelType from labels
      return updatedLabels;
    });
  
    setLabelTypes((prevLabelTypes) => {
      const updatedLabelTypes = { ...prevLabelTypes };
      delete updatedLabelTypes[labelType]; // Remove the label type from labelTypes
      return updatedLabelTypes;
    });
  
    showSuccessMessage(`Label type "${labelType}" and all related labels have been deleted.`);
  };
  

  const deleteLabel = (labelToDelete, labelType) => {
    // First, delete the label in all verbatims and get the number of affected verbatims
    const affectedVerbatimsCount = deleteLabelInVerbatims(labelToDelete, labelType);
  
    // Now update the labels and labelTypes based on the label deletion
    setLabels((prevLabels) => {
      const updatedLabels = { ...prevLabels };
  
      // Check if the labelType exists in labels and proceed to delete the label from it
      if (updatedLabels[labelType]) {
        // Filter out the labelToDelete from the labelType array
        updatedLabels[labelType] = updatedLabels[labelType].filter((l) => l.name !== labelToDelete);
  
        // If no labels remain in this label type, remove the label type entirely
        if (updatedLabels[labelType].length === 0) {
          // Update labelTypes by removing the labelType as well
          setLabelTypes((prevLabelTypes) => {
            const updatedLabelTypes = { ...prevLabelTypes };
            delete updatedLabelTypes[labelType]; // Remove the label type from labelTypes
            return updatedLabelTypes;
          });
  
          delete updatedLabels[labelType]; // Remove the empty labelType from labels
        }
      }
  
      // Return the updated labels object to update the state
      return updatedLabels;
    });
  
    // Show success message with the number of affected verbatims
    showSuccessMessage(
      `Label "${labelToDelete}" deleted successfully. ${affectedVerbatimsCount} verbatims affected.`
    );
  };  

  // Update label's keywords
  const updateKeywords = (labelType, label, newKeywords) => {
    if (!Array.isArray(newKeywords)) {
      showSuccessMessage('Invalid input for keywords!');
      return;
    }

    const formattedKeywords = newKeywords.map((kw) => kw.trim()).filter((kw) => kw.length > 0);

    setLabels((prevLabels) => {
      const updatedLabels = { ...prevLabels };

      if (labelType && Array.isArray(updatedLabels[labelType])) {
        const labelIndex = updatedLabels[labelType].findIndex((l) => l.name === label);

        if (labelIndex !== -1) {
          updatedLabels[labelType][labelIndex].keywords = formattedKeywords;
          showSuccessMessage('Keywords updated successfully!');
        } else {
          showSuccessMessage('Label not found!');
        }
      } else {
        showSuccessMessage('Label type not found!');
      }

      return updatedLabels;
    });
  };

  // Export the setup to XLSX format
  const exportSetup = () => {
    exportToXLSX('setup_data.xlsx', { labels, labelTypes });
    showSuccessMessage('Setup exported successfully!');
  };

  // Handle Excel file import and update the setup
  const handleFileSelect = (event) => {
    const file = event.target.files[0];  // Access the first file from the input event
    if (file) {
      const reader = new FileReader();
      reader.onload = function (e) {
        const data = new Uint8Array(e.target.result);
        loadFromExcel(data, setLabels, setLabelTypes, defaultLabels, defaultLabelTypes);
      };
      reader.readAsArrayBuffer(file);  // Use the selected file as the Blob for reading
    }
  };


  // Delete all labels and label types
  const deleteAllLabels = () => {
    if (window.confirm('Are you sure you want to delete all stored labels?')) {
      setLabels({});
      setLabelTypes({});
      showSuccessMessage('All labels and label types deleted successfully.');
    }
  };

  if (isLoading) {
    return <div>Loading setup data...</div>;
  }

  return (
    <SetupContext.Provider
      value={{
        labels,
        setLabels,
        labelTypes,
        setLabelTypes,
        defaultLabels,
        defaultLabelTypes,
        handleEditLabelType,
        handleEditLabel,
        handleMandatoryChange,
        handleSentimentChange,
        handleMultiValueChange,
        addLabel,
        deleteLabelType,
        deleteLabel,
        updateKeywords,
        mergeLabelTypes,
        exportSetup,
        handleFileSelect,
        deleteAllLabels,
        statusMessage,
        showSuccessMessage,
      }}
    >
      {children}
    </SetupContext.Provider>
  );
};
